import { Box, Button, Card, CardContent, CircularProgress, FormControl, MenuItem, Select, Typography } from '@mui/material';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { mindtimeBackupsApi } from 'src/api/mindtime/backupsApi';
import {
  BackupSetsGraphResponse,
  QuotaHistoryResponse,
  BackupsetHistoryResponse,
  StorageDestination,
} from 'src/types/mindtime/backups';
import {
  backupsDonutColors,
  formatMonthLabel,
  formatStorageMbAxisLabel,
  getInitialQuotaFrom,
  getInitialQuotaTo,
  getMonthOptions,
  getStorageMbYAxisScale,
  type BackupsChartsActiveTab,
} from '../../utils/backupsChartsCardUtils';

const cardSx = {
  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.08)',
  border: '1px solid #EDF2F7',
  borderRadius: '8px',
};

interface BackupsChartsCardProps {
  destinations?: StorageDestination[];
  organizationId?: string;
}

const BackupsChartsCard = ({
  destinations = [],
  organizationId,
}: BackupsChartsCardProps) => {
  // Initialize destination state with first enabled destination, or first destination
  const getDefaultDestination = () => {
    if (!destinations || destinations.length === 0) return '';
    const enabledDest = destinations.find((d) => d.enabled);
    return enabledDest?.name || destinations[0]?.name || '';
  };

  const [destination, setDestination] = useState(getDefaultDestination());
  const [activeTab, setActiveTab] = useState<BackupsChartsActiveTab>('quota');
  const [graphData, setGraphData] = useState<BackupSetsGraphResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track which quota slices are hidden in the donut chart
  const [hiddenQuotaSliceKeys, setHiddenQuotaSliceKeys] = useState<Set<string>>(() => new Set());

  // Quota history state (from/to in YYYY-MM)
  const [quotaFrom, setQuotaFrom] = useState<string>(getInitialQuotaFrom);
  const [quotaTo, setQuotaTo] = useState<string>(getInitialQuotaTo);
  const [quotaHistory, setQuotaHistory] = useState<QuotaHistoryResponse | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [quotaError, setQuotaError] = useState<string | null>(null);

  // Backupset history state (independent from/to in YYYY-MM)
  const [backupHistoryFrom, setBackupHistoryFrom] = useState<string>(getInitialQuotaFrom);
  const [backupHistoryTo, setBackupHistoryTo] = useState<string>(getInitialQuotaTo);
  const [backupsetHistory, setBackupsetHistory] = useState<BackupsetHistoryResponse | null>(null);
  const [backupsetHistoryLoading, setBackupsetHistoryLoading] = useState(false);
  const [backupsetHistoryError, setBackupsetHistoryError] = useState<string | null>(null);

  const monthOptions = getMonthOptions();

  const toggleQuotaSliceVisibility = (key: string) => {
    setHiddenQuotaSliceKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleQuotaFromChange = (value: string) => {
    setQuotaFrom(value);
    if (value > quotaTo) {
      setQuotaTo(value);
    }
  };

  const handleQuotaToChange = (value: string) => {
    setQuotaTo(value);
    if (value < quotaFrom) {
      setQuotaFrom(value);
    }
  };

  const handleBackupHistoryFromChange = (value: string) => {
    setBackupHistoryFrom(value);
    if (value > backupHistoryTo) {
      setBackupHistoryTo(value);
    }
  };

  const handleBackupHistoryToChange = (value: string) => {
    setBackupHistoryTo(value);
    if (value < backupHistoryFrom) {
      setBackupHistoryFrom(value);
    }
  };

  // Update destination when destinations prop changes
  useEffect(() => {
    const defaultDest = getDefaultDestination();
    if (defaultDest) {
      setDestination(defaultDest);
    }
  }, [destinations]);

  // Fetch graph data when quota tab is active
  useEffect(() => {
    if (activeTab === 'quota') {
      setLoading(true);
      setError(null);
      mindtimeBackupsApi
        .getBackupSetsGraph(organizationId)
        .then((data) => {
          setGraphData(data);
          setLoading(false);
        })
        .catch((err) => {
          console.error('Failed to fetch backup sets graph:', err);
          setError(err.message || 'Failed to load backup sets data');
          setLoading(false);
        });
    }
  }, [activeTab, organizationId]);

  // Fetch quota history when Quota history tab is active or date range changes
  useEffect(() => {
    if (activeTab !== 'quotaHistory') return;
    setQuotaLoading(true);
    setQuotaError(null);
    mindtimeBackupsApi
      .getQuotaHistory(quotaFrom, quotaTo, organizationId)
      .then((data) => {
        setQuotaHistory(data);
        setQuotaLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch quota history:', err);
        setQuotaError(err.message || 'Failed to load quota history');
        setQuotaLoading(false);
      });
  }, [activeTab, quotaFrom, quotaTo, organizationId]);

  // Fetch backupset history when Backupset history tab is active or date range changes
  useEffect(() => {
    if (activeTab !== 'history') return;
    setBackupsetHistoryLoading(true);
    setBackupsetHistoryError(null);
    mindtimeBackupsApi
      .getBackupsetHistory(backupHistoryFrom, backupHistoryTo, organizationId)
      .then((data) => {
        setBackupsetHistory(data);
        setBackupsetHistoryLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch backupset history:', err);
        setBackupsetHistoryError(err.message || 'Failed to load backupset history');
        setBackupsetHistoryLoading(false);
      });
  }, [activeTab, backupHistoryFrom, backupHistoryTo, organizationId]);

  // Donut chart for Backupset quota tab
  const renderDonutChart = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 400 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 400 }}>
          <Typography variant="body2" color="error">
            {error}
          </Typography>
        </Box>
      );
    }

    if (!graphData || !graphData.slices || graphData.slices.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 400 }}>
          <Typography variant="body2" color="textSecondary">
            No backup sets found
          </Typography>
        </Box>
      );
    }

    const { slices } = graphData;

    // Build full legend items (all backup sets + optional free space slice)
    const sliceItems = slices.map((s, index) => ({
      key: s.backupSetId || `slice-${index}`,
      label: s.name,
      value: s.usedGB,
      color: backupsDonutColors[index % backupsDonutColors.length],
      type: 'slice' as const,
      metadata: s,
    }));

    const hasAvailableSlice = graphData.availableGB !== null && graphData.availableGB > 0;
    const freeSpaceItem = hasAvailableSlice
      ? {
        key: '__available__',
        label: 'Free space',
        value: graphData.availableGB as number,
        color: '#E2E8F0',
        type: 'available' as const,
      }
      : null;

    const allLegendItems = hasAvailableSlice && freeSpaceItem
      ? [...sliceItems, freeSpaceItem]
      : sliceItems;

    // Filter items that are currently visible in the chart
    const visibleItems = allLegendItems.filter((item) => !hiddenQuotaSliceKeys.has(item.key));

    const finalLabels = visibleItems.map((item) => item.label);
    const finalSeries = visibleItems.map((item) => item.value);
    const finalColors = visibleItems.map((item) => item.color);

    // Total used (only backup-set slices, ignore free space)
    const totalUsed = visibleItems
      .filter((item) => item.type === 'slice')
      .reduce((sum, item) => sum + item.value, 0);

    const donutOptions: ApexOptions = {
      chart: {
        type: 'donut',
        toolbar: { show: false },
        fontFamily: 'inherit',
      },
      labels: finalLabels,
      colors: finalColors,
      dataLabels: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          donut: {
            // Slightly smaller so the circle is fully visible
            size: '70%',
            labels: {
              show: true,
              name: {
                show: false,
              },
              value: {
                show: false,
              },
              total: {
                show: true,
                label: 'GB USED',
                fontSize: '18px',
                fontWeight: 600,
                color: '#4299E1',
                formatter: () => totalUsed.toFixed(0),
              },
            },
          },
        },
      },
      legend: {
        show: false,
      },
      tooltip: {
        theme: 'light',
        custom: ({ seriesIndex }) => {
          const item = visibleItems[seriesIndex];

          if (!item) {
            return '';
          }

          if (item.type === 'available') {
            let tooltipContent = `
              <div style="padding: 10px; background: white; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                <div style="font-weight: 600; font-size: 14px; color: #2D3748; margin-bottom: 6px;">Free space</div>
                <div style="font-size: 13px; color: #4A5568;">${item.value.toFixed(2)} GB</div>
            `;
            tooltipContent += `</div>`;
            return tooltipContent;
          }

          // Handle regular backup set slices
          if (item.type === 'slice' && item.metadata) {
            const slice = item.metadata;
            let tooltipContent = `
              <div style="padding: 10px; background: white; border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
                <div style="font-weight: 600; font-size: 14px; color: #2D3748; margin-bottom: 6px;">${slice.name}</div>
                <div style="font-size: 13px; color: #4A5568; margin-bottom: 4px;">${slice.usedGB.toFixed(2)} GB</div>
            `;

            if (slice.type) {
              tooltipContent += `<div style="font-size: 12px; color: #718096; margin-bottom: 2px;">Type: ${slice.type}</div>`;
            }

            if (slice.destinationName) {
              tooltipContent += `<div style="font-size: 12px; color: #718096;">Destination: ${slice.destinationName}</div>`;
            }

            tooltipContent += `</div>`;
            return tooltipContent;
          }

          return '';
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            legend: {
              position: 'bottom',
            },
          },
        },
      ],
    };

    return (
      <Box px={2.5} py={2}>
        <Box
          sx={{
            height: 400,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch',
            gap: 1,
          }}
        >
          <Box
            sx={{
              flex: { xs: '0 0 auto', md: '0 0 70%' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {finalSeries.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No slices selected. Click items in the list to show them.
              </Typography>
            ) : (
              <Box
                sx={{
                  width: { xs: 260, md: 320 },
                  height: { xs: 260, md: 320 },
                }}
              >
                <Chart
                  options={donutOptions}
                  series={finalSeries}
                  type="donut"
                  width="100%"
                  height="100%"
                />
              </Box>
            )}
          </Box>

          <Box
            sx={{
              flex: { xs: '0 0 auto', md: '0 0 30%' },
              maxWidth: { md: 250 },
              overflowY: 'auto',
              borderLeft: { md: '1px solid #E2E8F0' },
              borderTop: { xs: '1px solid #E2E8F0', md: 'none' },
              pt: { xs: 1.5, md: 0 },
              pl: { xs: 0, md: 2 },
            }}
          >
            {allLegendItems.map((item) => {
              const isHidden = hiddenQuotaSliceKeys.has(item.key);

              return (
                <Box
                  key={item.key}
                  onClick={() => toggleQuotaSliceVisibility(item.key)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 0.75,
                    cursor: 'pointer',
                    opacity: isHidden ? 0.4 : 1,
                    '&:hover': {
                      opacity: isHidden ? 0.7 : 0.85,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '10px',
                      bgcolor: item.color,
                      mr: 1,
                    }}
                  />
                  <Typography sx={{ fontSize: 12, color: '#2D3748' }}>
                    {`${item.label} - ${item.value.toFixed(1)} GB`}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    );
  };

  // Line chart for Backupset history tab
  const renderLineChart = () => {
    if (backupsetHistoryLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 400 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (backupsetHistoryError) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 400 }}>
          <Typography variant="body2" color="error">
            {backupsetHistoryError}
          </Typography>
        </Box>
      );
    }

    // Support both wrapped and raw shapes: { Status, Data: { backupSets } } or { backupSets }
    const backupSets =
      (backupsetHistory as any)?.Data?.backupSets ?? (backupsetHistory as any)?.backupSets ?? [];

    // Build sorted unique list of dates across all backup sets
    const dateSet = new Set<string>();
    backupSets.forEach((set) => {
      set.storage.forEach((point) => {
        dateSet.add(point.date);
      });
    });
    const allDates = Array.from(dateSet).sort();

    const categories = allDates.map((d) =>
      new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
    );

    const seriesWithPeak = backupSets.map((set, index) => {
      const seriesData = allDates.map((date) => {
        const point = set.storage.find((p) => p.date === date);
        if (!point) {
          return 0;
        }
        const raw = Number(point.totalStorageRawSize);
        if (!Number.isFinite(raw) || raw <= 0) {
          return 0;
        }
        // Convert bytes to MB with 2 decimal places
        const mb = raw / (1024 * 1024);
        return Math.round(mb * 100) / 100;
      });

      const seriesPeak = seriesData.reduce((peak, v) => Math.max(peak, v), 0);

      return {
        name: set.backupSetName || set.backupSetId || `Backupset ${index + 1}`,
        data: seriesData,
        seriesPeak,
        color: backupsDonutColors[index % backupsDonutColors.length],
      };
    });

    // Apex draws earlier series behind later ones — largest first, smallest on top.
    const sortedSeries = [...seriesWithPeak].sort((a, b) => b.seriesPeak - a.seriesPeak);
    const series = sortedSeries.map(({ name, data }) => ({ name, data }));
    const lineColors = sortedSeries.map((s) => s.color);

    const peakMb = seriesWithPeak.reduce(
      (peak, s) => Math.max(peak, s.seriesPeak),
      0
    );
    const yAxisScale = getStorageMbYAxisScale(peakMb);

    const lineChartOptions: ApexOptions = {
      chart: {
        type: 'line',
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: 'inherit',
      },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth', width: 2 },
      colors: lineColors,
      xaxis: {
        categories,
        labels: { style: { colors: '#718096', fontSize: '11px' } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        min: yAxisScale.min,
        max: yAxisScale.max,
        tickAmount: yAxisScale.tickAmount,
        forceNiceScale: false,
        labels: {
          formatter: (val: number) => formatStorageMbAxisLabel(val, yAxisScale.step),
          style: { colors: '#718096', fontSize: '11px' },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      grid: {
        borderColor: '#EDF2F7',
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
      },
      legend: { show: false },
      tooltip: { theme: 'light' },
    };

    return (
      <Box px={2.5} py={2}>
        <Typography variant="body2" sx={{ fontSize: 12, color: '#718096', mb: 2 }}>
          Storage usage per backupset over the last 90 days
        </Typography>
        <Box sx={{ height: 280, mb: 2 }}>
          <Chart options={lineChartOptions} series={series} type="line" height={280} />
        </Box>
      </Box>
    );
  };

  // Area chart for Quota history tab (driven by quota-distribution API)
  const renderAreaChart = () => {
    if (quotaLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 400 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (quotaError) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" sx={{ height: 400 }}>
          <Typography variant="body2" color="error">
            {quotaError}
          </Typography>
        </Box>
      );
    }

    // Support both wrapped and raw shapes: { Status, Data: { Storage } } or { Storage }
    const storagePoints =
      quotaHistory?.Data?.Storage ?? (quotaHistory as any)?.Storage ?? [];

    const areaChartCategories = storagePoints.map((p) =>
      new Date(p.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
    );
    const areaChartDataMB = storagePoints.map((p) => Math.round((p.totalRaw / (1024 * 1024)) * 100) / 100);

    const peakMb = areaChartDataMB.reduce((peak, v) => Math.max(peak, v), 0);
    const yAxisScale = getStorageMbYAxisScale(peakMb);

    const areaChartOptions: ApexOptions = {
      chart: {
        type: 'area',
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: 'inherit',
      },
      dataLabels: { enabled: false },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.45,
          opacityTo: 0.15,
        },
      },
      colors: ['#63B3ED'],
      stroke: { curve: 'smooth', width: 2 },
      xaxis: {
        categories: areaChartCategories,
        labels: { style: { colors: '#718096', fontSize: '11px' } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        min: yAxisScale.min,
        max: yAxisScale.max,
        tickAmount: yAxisScale.tickAmount,
        forceNiceScale: false,
        labels: {
          style: { colors: '#718096', fontSize: '11px' },
          formatter: (val: number) => formatStorageMbAxisLabel(val, yAxisScale.step),
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      grid: {
        borderColor: '#EDF2F7',
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
      },
      tooltip: {
        theme: 'light',
        y: {
          formatter: (val: number) => formatStorageMbAxisLabel(val, yAxisScale.step),
        },
      },
    };

    const areaChartSeries = [{ name: 'Total storage (MB)', data: areaChartDataMB }];

    return (
      <Box px={2.5} py={2}>
        <Typography variant="body2" sx={{ fontSize: 12, color: '#718096', fontWeight: 600, mb: 1 }}>
          Aggregate Quota Usage
        </Typography>
        <Box sx={{ height: 280, mb: 2 }}>
          <Chart options={areaChartOptions} series={areaChartSeries} type="area" height={280} />
        </Box>
        <Typography variant="caption" sx={{ fontSize: 11, color: '#718096' }}>
          Values represent total storage (MB) per day for the selected period.
        </Typography>
      </Box>
    );
  };

  const tabLabels: Record<BackupsChartsActiveTab, string> = {
    quota: 'Backupset quota',
    history: 'Backupset history',
    quotaHistory: 'Quota history',
  };

  const tabChartContent: Record<BackupsChartsActiveTab, () => React.ReactNode> = {
    quota: renderDonutChart,
    history: renderLineChart,
    quotaHistory: renderAreaChart,
  };

  return (
    <Card sx={cardSx}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        {/* Header: title + DESTINATION dropdown + tabs + date range */}
        <Box
          display="flex"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="space-between"
          px={2.5}
          pt={2}
          pb={1.5}
          borderBottom="1px solid #EDF2F7"
          sx={{ gap: 2 }}
        >
          <Box display="flex" alignItems="center" sx={{ gap: 1 }}>
            <CloudQueueIcon sx={{ fontSize: 18, color: '#3182CE' }} />
            <Typography variant="h6" style={{ fontWeight: 600, color: '#2D3748' }}>
              Storage Distribution
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" flexWrap="wrap" sx={{ gap: 1.5 }}>
            <Box display="flex" alignItems="center" sx={{ gap: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: 11, color: '#718096', fontWeight: 600 }}>
                DESTINATION:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 140, ml: 0.5 }}>
                <Select
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  sx={{ fontSize: 12, height: 32 }}
                >
                  {destinations.length > 0 ? (
                    destinations.map((dest) => (
                      <MenuItem key={dest.name} value={dest.name}>
                        {dest.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      No destinations available
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Box>
            {(activeTab === 'quotaHistory' || activeTab === 'history') && (
              <Box display="flex" alignItems="center" sx={{ gap: 0.5, border: '1px solid #E2E8F0', borderRadius: 1, px: 1, py: 0.5 }}>
                <CalendarTodayIcon sx={{ fontSize: 16, color: '#718096' }} />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={activeTab === 'quotaHistory' ? quotaFrom : backupHistoryFrom}
                    onChange={(e) =>
                      (activeTab === 'quotaHistory'
                        ? handleQuotaFromChange
                        : handleBackupHistoryFromChange)(e.target.value as string)
                    }
                    sx={{ fontSize: 11, height: 28 }}
                  >
                    {monthOptions.map((m) => (
                      <MenuItem key={`from-${m}`} value={m}>
                        {formatMonthLabel(m)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Typography variant="body2" sx={{ fontSize: 12, color: '#2D3748', mx: 0.5 }}>
                  to
                </Typography>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <Select
                    value={activeTab === 'quotaHistory' ? quotaTo : backupHistoryTo}
                    onChange={(e) =>
                      (activeTab === 'quotaHistory'
                        ? handleQuotaToChange
                        : handleBackupHistoryToChange)(e.target.value as string)
                    }
                    sx={{ fontSize: 11, height: 28 }}
                  >
                    {monthOptions.map((m) => (
                      <MenuItem key={`to-${m}`} value={m}>
                        {formatMonthLabel(m)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
            <Box display="flex" sx={{ gap: 0.5 }}>
              {(Object.keys(tabLabels) as BackupsChartsActiveTab[]).map((key) => (
                <Button
                  key={key}
                  size="small"
                  variant={activeTab === key ? 'contained' : 'outlined'}
                  onClick={() => setActiveTab(key)}
                  sx={{
                    fontSize: 11,
                    textTransform: 'none',
                    minWidth: 'auto',
                    px: 1.5,
                    height: 28,
                    ...(activeTab === key
                      ? { bgcolor: '#3182CE', color: '#fff', '&:hover': { bgcolor: '#2C5282' } }
                      : { borderColor: '#E2E8F0', color: '#4A5568' }),
                  }}
                >
                  {tabLabels[key]}
                </Button>
              ))}
            </Box>

          </Box>
        </Box>

        {/* Tab content */}
        {tabChartContent[activeTab]()}
      </CardContent>
    </Card>
  );
};

export default BackupsChartsCard;
